import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { analyticsStatus, shopifyStatus, socialStatus, getKlaviyoStatus } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { PlugsConnected, CheckCircle, Flask } from "@phosphor-icons/react";

export default function IntegrationsPage() {
  const { activeId } = useClient();
  const navigate = useNavigate();
  const aQ = useQuery({ queryKey: ["analytics-status", activeId], queryFn: () => analyticsStatus(activeId), enabled: !!activeId });
  const sQ = useQuery({ queryKey: ["shopify-status", activeId], queryFn: () => shopifyStatus(activeId), enabled: !!activeId });
  const soQ = useQuery({ queryKey: ["social-status", activeId], queryFn: () => socialStatus(activeId), enabled: !!activeId });
  const kQ = useQuery({ queryKey: ["klaviyo-status", activeId], queryFn: () => getKlaviyoStatus(activeId), enabled: !!activeId });

  const items = [
    { name: "Shopify", category: "Store", connected: !!sQ.data?.connected, to: "/shopify",
      desc: "Products, orders, customers and revenue — synced read-only from your store.",
      empty: "Connect Shopify to view products, orders, customers and revenue." },
    { name: "Google Analytics", category: "Analytics", connected: !!aQ.data?.ga4?.connected, to: "/ga4",
      desc: "Users, sessions, traffic sources and conversions.",
      empty: "Connect Google Analytics to view traffic data." },
    { name: "Meta Ads", category: "Advertising", connected: !!aQ.data?.meta?.connected, to: "/ads",
      desc: "Ad spend, campaigns, clicks, conversions and ROAS from Meta.",
      empty: "Connect Meta Ads to view ad spend and ROAS." },
    { name: "Google Ads", category: "Advertising", connected: !!aQ.data?.google?.connected, to: "/ads",
      desc: "Campaign performance, spend and conversions from Google Ads.",
      empty: "Connect Google Ads to view campaign performance." },
    { name: "Facebook & Instagram", category: "Publishing", connected: !!soQ.data?.connected, testMode: !!soQ.data?.test_mode, to: "/social",
      desc: "Publishing connection used by Auto Pilot and manual posting.",
      empty: "Connect Facebook & Instagram to publish social content." },
    { name: "Klaviyo", category: "Email & SMS", connected: !!kQ.data?.connected, testMode: !!kQ.data?.test_mode, to: "/comms",
      desc: "Email flows, campaigns and list growth.",
      empty: "Connect Klaviyo to view live email performance." },
  ];
  const cats = [...new Set(items.map((i) => i.category))];

  return (
    <PageContainer>
      <PageHeader eyebrow="Data Sources" title="Integrations"
        subtitle="Live connection status for this workspace. Screens only show verified data from connected sources — nothing is simulated or sampled." />
      <div className="space-y-6">
        {cats.map((cat) => (
          <div key={cat}>
            <div className="label-caps mb-3">{cat}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.filter((i) => i.category === cat).map((it) => (
                <div key={it.name} className={`card-matte p-5 ${it.connected && !it.testMode ? "brand-glow" : ""}`} data-testid="integration-card">
                  <div className="flex items-start justify-between">
                    <div className="h-11 w-11 rounded-xl bg-white/5 flex items-center justify-center">
                      <PlugsConnected size={22} className={it.connected ? "brand-text" : "text-[#9a9a9a]"} weight={it.connected ? "fill" : "regular"} />
                    </div>
                    {it.connected && (it.testMode ? (
                      <span className="flex items-center gap-1 text-[11px] text-amber-400 font-semibold" data-testid={`status-${it.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}><Flask size={14} weight="fill" /> Test mode</span>
                    ) : (
                      <span className="flex items-center gap-1 text-[11px] text-emerald-400 font-semibold" data-testid={`status-${it.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}><CheckCircle size={14} weight="fill" /> Connected</span>
                    ))}
                  </div>
                  <div className="font-head font-semibold mt-3">{it.name}</div>
                  <div className="text-sm text-[#9a9a9a] mt-1 min-h-[40px]">{it.connected ? it.desc : it.empty}</div>
                  <Button data-testid={`connect-${it.name.toLowerCase().replace(/[^a-z]+/g, "-")}-btn`}
                    onClick={() => navigate(it.to)}
                    variant={it.connected ? "outline" : "default"}
                    className={`w-full mt-3 ${it.connected ? "border-white/15 bg-transparent hover:bg-white/5 text-white" : "bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"}`}>
                    {it.connected ? "Manage" : "Connect"}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PageContainer>
  );
}
